import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import customerService, { Customer, CustomerAnalytics, CustomerFilters } from '../services/customerService';
import { toJsDate } from '../utils/firestoreDate';
import { useNotifications } from './NotificationContext';
import logger from '../utils/logger';

interface CustomerState {
  customers: Customer[];
  selectedCustomer: Customer | null;
  loading: boolean;
  error: string | null;
  analytics: CustomerAnalytics | null;
  filters: CustomerFilters;
  searchTerm: string;
  viewMode: 'grid' | 'list';
  showFilters: boolean;
  showCustomerModal: boolean;
  showAddCustomerModal: boolean;
  showEditCustomerModal: boolean;
  showDeleteConfirmModal: boolean;
  customerToDelete: Customer | null;
}

type CustomerAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CUSTOMERS'; payload: Customer[] }
  | { type: 'SET_SELECTED_CUSTOMER'; payload: Customer | null }
  | { type: 'SET_ANALYTICS'; payload: CustomerAnalytics }
  | { type: 'SET_FILTERS'; payload: Partial<CustomerFilters> }
  | { type: 'SET_SEARCH_TERM'; payload: string }
  | { type: 'SET_VIEW_MODE'; payload: 'grid' | 'list' }
  | { type: 'SET_SHOW_FILTERS'; payload: boolean }
  | { type: 'SET_SHOW_CUSTOMER_MODAL'; payload: boolean }
  | { type: 'SET_SHOW_ADD_CUSTOMER_MODAL'; payload: boolean }
  | { type: 'SET_SHOW_EDIT_CUSTOMER_MODAL'; payload: boolean }
  | { type: 'SET_SHOW_DELETE_CONFIRM_MODAL'; payload: boolean }
  | { type: 'SET_CUSTOMER_TO_DELETE'; payload: Customer | null }
  | { type: 'ADD_CUSTOMER'; payload: Customer }
  | { type: 'UPDATE_CUSTOMER'; payload: Customer }
  | { type: 'DELETE_CUSTOMER'; payload: string }
  | { type: 'RESET_STATE' };

const initialState: CustomerState = {
  customers: [],
  selectedCustomer: null,
  loading: false,
  error: null,
  analytics: null,
  filters: {
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  },
  searchTerm: '',
  viewMode: 'grid',
  showFilters: false,
  showCustomerModal: false,
  showAddCustomerModal: false,
  showEditCustomerModal: false,
  showDeleteConfirmModal: false,
  customerToDelete: null
};

function customerReducer(state: CustomerState, action: CustomerAction): CustomerState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CUSTOMERS':
      return { ...state, customers: action.payload };
    case 'SET_SELECTED_CUSTOMER':
      return { ...state, selectedCustomer: action.payload };
    case 'SET_ANALYTICS':
      return { ...state, analytics: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.payload };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'SET_SHOW_FILTERS':
      return { ...state, showFilters: action.payload };
    case 'SET_SHOW_CUSTOMER_MODAL':
      return { ...state, showCustomerModal: action.payload };
    case 'SET_SHOW_ADD_CUSTOMER_MODAL':
      return { ...state, showAddCustomerModal: action.payload };
    case 'SET_SHOW_EDIT_CUSTOMER_MODAL':
      return { ...state, showEditCustomerModal: action.payload };
    case 'SET_SHOW_DELETE_CONFIRM_MODAL':
      return { ...state, showDeleteConfirmModal: action.payload };
    case 'SET_CUSTOMER_TO_DELETE':
      return { ...state, customerToDelete: action.payload };
    case 'ADD_CUSTOMER':
      return { ...state, customers: [...state.customers, action.payload] };
    case 'UPDATE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.map(customer =>
          customer.id === action.payload.id ? action.payload : customer
        ),
        selectedCustomer: state.selectedCustomer?.id === action.payload.id ? action.payload : state.selectedCustomer
      };
    case 'DELETE_CUSTOMER':
      return {
        ...state,
        customers: state.customers.filter(customer => customer.id !== action.payload),
        selectedCustomer: state.selectedCustomer?.id === action.payload ? null : state.selectedCustomer
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
}

interface CustomerContextType {
  state: CustomerState;
  dispatch: React.Dispatch<CustomerAction>;
  // Actions
  fetchCustomers: () => Promise<void>;
  fetchCustomerAnalytics: () => Promise<void>;
  createCustomer: (customerData: Omit<Customer, 'id' | 'createdAt' | 'totalOrders' | 'totalSpent' | 'lastOrderDate'>) => Promise<void>;
  updateCustomer: (id: string, customerData: Partial<Customer>) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;
  updateCustomerStatus: (id: string, status: Customer['status']) => Promise<void>;
  addCustomerNote: (id: string, note: string) => Promise<void>;
  addCustomerTag: (id: string, tag: string) => Promise<void>;
  removeCustomerTag: (id: string, tag: string) => Promise<void>;
  updateCustomerPreferences: (id: string, preferences: Customer['preferences']) => Promise<void>;
  searchCustomers: (filters: CustomerFilters) => Promise<void>;
  exportCustomers: (filters?: CustomerFilters) => Promise<void>;
  bulkUpdateCustomerStatus: (customerIds: string[], status: Customer['status']) => Promise<void>;
  sendCustomerMessage: (customerId: string, message: string, type: 'email' | 'sms' | 'notification') => Promise<void>;
  // Checkout integration
  createCustomerFromCheckout: (checkoutData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    shippingAddress: {
      firstName: string;
      lastName: string;
      address: string;
      address2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      email?: string;
      company?: string;
      deliveryInstructions?: string;
      phone?: string;
    };
    billingAddress: {
      firstName: string;
      lastName: string;
      email: string;
      address: string;
      address2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      company?: string;
      notes?: string;
    };
    orderId?: string;
    orderNumber?: string;
    totalAmount?: number;
  }) => Promise<Customer>;
  updateCustomerFromOrder: (customerId: string, orderData: {
    orderId?: string;
    orderNumber?: string;
    totalAmount?: number;
    lastOrderDate?: Date;
    orderDetails?: any;
  }) => Promise<Customer>;
  // UI Actions
  selectCustomer: (customer: Customer | null) => void;
  setSearchTerm: (term: string) => void;
  setViewMode: (mode: 'grid' | 'list') => void;
  toggleFilters: () => void;
  openCustomerModal: (customer: Customer) => void;
  closeCustomerModal: () => void;
  openAddCustomerModal: () => void;
  closeAddCustomerModal: () => void;
  openEditCustomerModal: (customer: Customer) => void;
  closeEditCustomerModal: () => void;
  openDeleteConfirmModal: (customer: Customer) => void;
  closeDeleteConfirmModal: () => void;
  // Computed values
  filteredCustomers: Customer[];
  customerAnalytics: {
    totalCustomers: number;
    activeCustomers: number;
    vipCustomers: number;
    totalRevenue: number;
    avgOrderValue: number;
  };
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export const useCustomerContext = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomerContext must be used within a CustomerProvider');
  }
  return context;
};

interface CustomerProviderProps {
  children: ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(customerReducer, initialState);
  const { addNotification } = useNotifications();

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const customers = await customerService.getAllCustomers();
      dispatch({ type: 'SET_CUSTOMERS', payload: customers });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to fetch customers' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Fetch customer analytics
  const fetchCustomerAnalytics = useCallback(async () => {
    try {
      const analytics = await customerService.getCustomerAnalytics();
      dispatch({ type: 'SET_ANALYTICS', payload: analytics });
    } catch (error) {
      logger.error('Error fetching customer analytics:', error);
    }
  }, []);

  // Create customer
  const createCustomer = async (customerData: Omit<Customer, 'id' | 'createdAt' | 'totalOrders' | 'totalSpent' | 'lastOrderDate'>) => {
    try {
      logger.debug('Creating customer with data:', customerData);
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const newCustomer = await customerService.createCustomer(customerData);
      logger.debug('Customer service returned:', newCustomer);
      dispatch({ type: 'ADD_CUSTOMER', payload: newCustomer });
      logger.debug('Customer added to state, closing modal');
      // Only close modal on success
      dispatch({ type: 'SET_SHOW_ADD_CUSTOMER_MODAL', payload: false });
      // Show success message (you can add a success toast here)
      logger.info('Customer created successfully:', newCustomer);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create customer';
      logger.error('Error in createCustomer:', error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      // Don't close modal on error - let user see the error and try again
      logger.error('Error creating customer:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Update customer
  const updateCustomer = async (id: string, customerData: Partial<Customer>) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const updatedCustomer = await customerService.updateCustomer(id, customerData);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
      dispatch({ type: 'SET_SHOW_EDIT_CUSTOMER_MODAL', payload: false });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update customer' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Delete customer
  const deleteCustomer = async (id: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      await customerService.deleteCustomer(id);
      dispatch({ type: 'DELETE_CUSTOMER', payload: id });
      dispatch({ type: 'SET_SHOW_DELETE_CONFIRM_MODAL', payload: false });
      dispatch({ type: 'SET_CUSTOMER_TO_DELETE', payload: null });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to delete customer' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Update customer status
  const updateCustomerStatus = async (id: string, status: Customer['status']) => {
    try {
      const updatedCustomer = await customerService.updateCustomerStatus(id, status);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update customer status' });
    }
  };

  // Add customer note
  const addCustomerNote = async (id: string, note: string) => {
    try {
      const updatedCustomer = await customerService.addCustomerNote(id, note);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add customer note' });
    }
  };

  // Add customer tag
  const addCustomerTag = async (id: string, tag: string) => {
    try {
      const updatedCustomer = await customerService.addCustomerTag(id, tag);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to add customer tag' });
    }
  };

  // Remove customer tag
  const removeCustomerTag = async (id: string, tag: string) => {
    try {
      const updatedCustomer = await customerService.removeCustomerTag(id, tag);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to remove customer tag' });
    }
  };

  // Update customer preferences
  const updateCustomerPreferences = async (id: string, preferences: Customer['preferences']) => {
    try {
      const updatedCustomer = await customerService.updateCustomerPreferences(id, preferences);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update customer preferences' });
    }
  };

  // Search customers
  const searchCustomers = async (filters: CustomerFilters) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const customers = await customerService.searchCustomers(filters);
      dispatch({ type: 'SET_CUSTOMERS', payload: customers });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to search customers' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Export customers
  const exportCustomers = async (filters?: CustomerFilters) => {
    try {
      const csvData = await customerService.exportCustomers(filters);
      // Create and download CSV file
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to export customers' });
    }
  };

  // Bulk update customer status
  const bulkUpdateCustomerStatus = async (customerIds: string[], status: Customer['status']) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await customerService.bulkUpdateCustomerStatus(customerIds, status);
      await fetchCustomers(); // Refresh the list
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to bulk update customer status' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Send customer message
  const sendCustomerMessage = async (customerId: string, message: string, type: 'email' | 'sms' | 'notification') => {
    try {
      await customerService.sendCustomerMessage(customerId, message, type);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to send customer message' });
    }
  };

  // Checkout integration
  const createCustomerFromCheckout = async (checkoutData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    shippingAddress: {
      firstName: string;
      lastName: string;
      address: string;
      address2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      email?: string;
      company?: string;
      deliveryInstructions?: string;
      phone?: string;
    };
    billingAddress: {
      firstName: string;
      lastName: string;
      email: string;
      address: string;
      address2?: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
      company?: string;
      notes?: string;
    };
    orderId?: string;
    orderNumber?: string;
    totalAmount?: number;
  }) => {
    try {
      logger.debug('=== CustomerContext: Creating customer from checkout ===');
      logger.debug('Checkout data:', checkoutData);
      logger.debug('Current state customers count:', state.customers.length);
      
      const newCustomer = await customerService.createCustomerFromCheckout(checkoutData);
      logger.debug('Customer created by service:', newCustomer);
      logger.debug('Customer ID:', newCustomer.id);
      logger.debug('Customer full name:', newCustomer.fullName);
      
      // Add to context state
      dispatch({ type: 'ADD_CUSTOMER', payload: newCustomer });
      logger.debug('Customer added to context state');
      logger.debug('New state customers count:', state.customers.length + 1);
      
      // Refresh analytics
      await fetchCustomerAnalytics();
      logger.debug('Customer analytics refreshed');

      addNotification({
        type: 'success',
        title: 'Customer Created',
        message: `Customer ${newCustomer.fullName} has been successfully added to Customer Management!`
      });
      
      return newCustomer;
    } catch (error) {
      logger.error('Error in createCustomerFromCheckout:', error);
      addNotification({
        type: 'error',
        title: 'Customer Creation Failed',
        message: 'Failed to create customer from checkout. Please try again.'
      });
      throw error; // Re-throw the error to be caught by the caller
    }
  };

  // Update customer from order
  const updateCustomerFromOrder = async (customerId: string, orderData: {
    orderId?: string;
    orderNumber?: string;
    totalAmount?: number;
    lastOrderDate?: Date;
    orderDetails?: any;
  }) => {
    try {
      logger.debug('=== CustomerContext: Updating customer from order ===');
      logger.debug('Order data for customer', customerId, orderData);
      const updatedCustomer = await customerService.updateCustomerFromOrder(customerId, orderData);
      logger.debug('Customer updated by service:', updatedCustomer);
      dispatch({ type: 'UPDATE_CUSTOMER', payload: updatedCustomer });
      logger.debug('Customer updated in context state');
      return updatedCustomer;
    } catch (error) {
      logger.error('Error in updateCustomerFromOrder:', error);
      addNotification({
        type: 'error',
        title: 'Customer Update Failed',
        message: 'Failed to update customer from order. Please try again.'
      });
      throw error; // Re-throw the error to be caught by the caller
    }
  };

  // UI Actions
  const selectCustomer = (customer: Customer | null) => {
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: customer });
  };

  const setSearchTerm = (term: string) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: term });
  };

  const setViewMode = (mode: 'grid' | 'list') => {
    dispatch({ type: 'SET_VIEW_MODE', payload: mode });
  };

  const toggleFilters = () => {
    dispatch({ type: 'SET_SHOW_FILTERS', payload: !state.showFilters });
  };

  const openCustomerModal = (customer: Customer) => {
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: customer });
    dispatch({ type: 'SET_SHOW_CUSTOMER_MODAL', payload: true });
  };

  const closeCustomerModal = () => {
    dispatch({ type: 'SET_SHOW_CUSTOMER_MODAL', payload: false });
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: null });
  };

  const openAddCustomerModal = () => {
    dispatch({ type: 'SET_SHOW_ADD_CUSTOMER_MODAL', payload: true });
  };

  const closeAddCustomerModal = () => {
    dispatch({ type: 'SET_SHOW_ADD_CUSTOMER_MODAL', payload: false });
  };

  const openEditCustomerModal = (customer: Customer) => {
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: customer });
    dispatch({ type: 'SET_SHOW_EDIT_CUSTOMER_MODAL', payload: true });
  };

  const closeEditCustomerModal = () => {
    dispatch({ type: 'SET_SHOW_EDIT_CUSTOMER_MODAL', payload: false });
    dispatch({ type: 'SET_SELECTED_CUSTOMER', payload: null });
  };

  const openDeleteConfirmModal = (customer: Customer) => {
    dispatch({ type: 'SET_CUSTOMER_TO_DELETE', payload: customer });
    dispatch({ type: 'SET_SHOW_DELETE_CONFIRM_MODAL', payload: true });
  };

  const closeDeleteConfirmModal = () => {
    dispatch({ type: 'SET_SHOW_DELETE_CONFIRM_MODAL', payload: false });
    dispatch({ type: 'SET_CUSTOMER_TO_DELETE', payload: null });
  };

  // Computed values
  const filteredCustomers = state.customers.filter(customer => {
    const searchTerm = state.searchTerm.toLowerCase();
    const fullName = customer.fullName?.toLowerCase() || '';
    const email = customer.email?.toLowerCase() || '';
    
    const matchesSearch = fullName.includes(searchTerm) || email.includes(searchTerm);
    const matchesStatus = state.filters.status === 'all' || customer.status === state.filters.status;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    let comparison = 0;
    switch (state.filters.sortBy) {
      case 'name':
        comparison = (a.fullName || '').localeCompare(b.fullName || '');
        break;
      case 'spent':
        comparison = (a.totalSpent || 0) - (b.totalSpent || 0);
        break;
      case 'orders':
        comparison = (a.totalOrders || 0) - (b.totalOrders || 0);
        break;
      case 'date': {
        const aT = (toJsDate(a.createdAt) ?? new Date(0)).getTime();
        const bT = (toJsDate(b.createdAt) ?? new Date(0)).getTime();
        comparison = aT - bT;
        break;
      }
      case 'status':
        comparison = (a.status || '').localeCompare(b.status || '');
        break;
    }
    return state.filters.sortOrder === 'asc' ? comparison : -comparison;
  });

  const customerAnalytics = {
    totalCustomers: state.customers.length,
    activeCustomers: state.customers.filter(c => c.status === 'active').length,
    vipCustomers: state.customers.filter(c => c.status === 'vip').length,
    totalRevenue: state.customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0),
    avgOrderValue: (() => {
      const totalRevenue = state.customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
      const totalOrders = state.customers.reduce((sum, c) => sum + (c.totalOrders || 0), 0);
      return totalOrders > 0 ? totalRevenue / totalOrders : 0;
    })()
  };

  // Load initial data
  useEffect(() => {
    fetchCustomers();
    fetchCustomerAnalytics();
  }, [fetchCustomers, fetchCustomerAnalytics]);

  // Monitor customer state changes
  useEffect(() => {
    logger.debug('=== CustomerContext State Change ===');
    logger.debug('Total customers in state:', state.customers.length);
    logger.debug('Customer IDs:', state.customers.map(c => ({ id: c.id, name: c.fullName, email: c.email })));
  }, [state.customers]);

  const value: CustomerContextType = {
    state,
    dispatch,
    fetchCustomers,
    fetchCustomerAnalytics,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    updateCustomerStatus,
    addCustomerNote,
    addCustomerTag,
    removeCustomerTag,
    updateCustomerPreferences,
    searchCustomers,
    exportCustomers,
    bulkUpdateCustomerStatus,
    sendCustomerMessage,
    createCustomerFromCheckout,
    updateCustomerFromOrder,
    selectCustomer,
    setSearchTerm,
    setViewMode,
    toggleFilters,
    openCustomerModal,
    closeCustomerModal,
    openAddCustomerModal,
    closeAddCustomerModal,
    openEditCustomerModal,
    closeEditCustomerModal,
    openDeleteConfirmModal,
    closeDeleteConfirmModal,
    filteredCustomers,
    customerAnalytics
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};
